<script setup lang="ts">
import { useLocale } from '@/plugins/translation';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { CoverFeatureItem, MaterialCover, MaterialPreview, CoverFeatures } from '@ilteducation/components';
import { ContentWithDuplicates } from '@ilteducation/content';
import { computed, ref } from 'vue';
import MaterialDialog from '@/components/material/MaterialDialog.vue';
import { useDisplay } from 'vuetify';
import ContentViewTable from '@ilteducation/components/src/collection/ContentViewTable.vue';
import { VDataTableVirtual } from 'vuetify/components';

const emits = defineEmits<{
  (e: 'load-more'): void;
}>();

const { t, l } = useLocale();
const { xs } = useDisplay();

const props = defineProps<{
  deduplicatedMaterials: MaterialV1[];
  isLoading: boolean;
  hasMore: boolean;
}>();
const selectedMaterial = ref<ContentWithDuplicates>();
const previewDialog = ref(false);
const contentTable = ref();

type ReadonlyHeaders = VDataTableVirtual['headers'];
const headers = computed(
  () =>
    [
      { key: 'id', value: 'id', sortable: false, align: 'start', width: '55px' },
      { title: '', key: 'title', value: 'title', sortable: false },
    ] as ReadonlyHeaders,
);

const getFeatures = (material: ContentWithDuplicates): CoverFeatureItem[] => {
  const features: CoverFeatureItem[] = [];
  if (material.duplicatesCount && material.duplicatesCount > 1) {
    features.push({
      id: 'languages',
      type: 'languages',
      color: 'rgb(var(--v-theme-secondary-container))',
      tooltip: t(l.material.available_languages_tooltip),
    });
  }
  return features;
};

const handleNext = () => {
  emits('load-more');
};

const onMaterialClick = (material: ContentWithDuplicates) => {
  selectedMaterial.value = material;
  previewDialog.value = true;
};

const onOpenContent = (id: string) => {
  const found = props.deduplicatedMaterials.find((material) => material.id === id);
  if (found) {
    onMaterialClick(found);
  }
};

const onChangeMaterial = (material: MaterialV1) => {
  selectedMaterial.value = material;
};
</script>

<template>
  <v-row v-if="!xs" class="ga-6 gc-10 gr-6 my-6">
    <template v-if="deduplicatedMaterials.length !== 0">
      <v-col v-for="material in deduplicatedMaterials" cols="auto" :key="material.id" class="pa-2">
        <MaterialPreview
          class="ma-2"
          :material="material"
          :duplicates-count="(material as ContentWithDuplicates).duplicatesCount"
          :hover-overflow-hidden="false"
          hover-rounded-class="rounded-sm"
          @on-material-click="onMaterialClick"
        >
          <template #coverFeatures>
            <CoverFeatures
              :features="getFeatures(material)"
              direction="vertical"
              position="left"
              :icon-size="20"
              size="large"
              :border="true"
              :extra-item-classes="['rounded-circle', 'feature-border']"
            >
            </CoverFeatures>
          </template>
        </MaterialPreview>
      </v-col>
    </template>
    <template v-if="deduplicatedMaterials.length === 0 && !isLoading">
      {{ t(l.label_no_matches) }}
    </template>
  </v-row>
  <v-row v-if="hasMore">
    <v-col class="d-flex justify-center">
      <v-btn
        :disabled="isLoading"
        color="primary"
        append-icon="mdi-chevron-down"
        variant="text"
        class="text-none"
        @click="handleNext"
      >
        {{ t(l.show_more) }}
      </v-btn>
      <v-progress-circular v-if="isLoading" class="ml-2" indeterminate size="30" color="primary" />
    </v-col>
  </v-row>
  <div v-if="xs" ref="contentTable" class="content-wrap">
    <ContentViewTable
      :parent-scroll-element="contentTable"
      :model-value="[]"
      :rounded="false"
      :content-items="deduplicatedMaterials"
      :heads="headers"
      :is-loading="isLoading"
      :has-more-content="hasMore"
      @open-content="onOpenContent"
      @on-load-content="handleNext"
    >
      <template #itemCover="{ item }">
        <MaterialCover
          :id="item.id"
          :alt="item.title"
          :duplicates-count="(item as ContentWithDuplicates).duplicatesCount"
          cover
        />
      </template>
    </ContentViewTable>
    <template v-if="deduplicatedMaterials.length === 0 && !isLoading">
      {{ t(l.label_no_matches) }}
    </template>
  </div>
  <MaterialDialog
    v-if="selectedMaterial"
    :model-value="previewDialog"
    :selected-material="selectedMaterial as MaterialV1"
    @update:model-value="(val) => (previewDialog = val)"
    @update:selected-material="onChangeMaterial"
  />
</template>

<style scoped lang="scss"></style>
